import { Component, Input, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MatLegacyMenuTrigger as MatMenuTrigger
} from '@angular/material/legacy-menu';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { SnackBarConfig } from 'v2/modules/shared/configs/snackbar.config';

import { IRuleCustomFieldValue } from '../../../shared/interfaces/custom-field.interface';
import { EFieldType } from '../../../shared/interfaces/field-type.interface';
import { trackById, trackByControlValue } from '../../../shared/util/track-by.util';
import { AutomationRuleConfig } from '../../config/automation-rule.config';
import { EAutomationRuleActions, EAutomationRuleEvents, EAutomationRuleConditions, EAutomationRuleActionTypes } from '../../interfaces';

type ActionIndex<ActionKey extends string, IndexValue> = {
  [Key in ActionKey] :IndexValue;
};

@Component({
  selector: 'lum-automation-rule-action-form',
  templateUrl: './automation-rule-action-form.component.html',
  styleUrls: ['./automation-rule-action-form.component.scss'],
})
export class AutomationRuleActionFormComponent implements OnInit, OnDestroy {
  @Input()
  form :UntypedFormGroup;

  @Input()
  customFields :IRuleCustomFieldValue[];

  @Input()
  hasUserGroups :boolean = false;

  trackById = trackById;
  trackByControlValue = trackByControlValue;
  automationRuleConfig = AutomationRuleConfig;
  rawAutomationRuleConfigActions = AutomationRuleConfig.actions;
  eAutomationRuleActions = EAutomationRuleActions;

  columnFields :any[] = [];
  customAggregateAttributes :{ id :string; name :string; type :string }[] = [];
  customSelected :string;

  aggregateAttributes :{ id :string; name :string; type :string }[] = [];
  attributeRelation :string = '';
  contextValues :string = '';

  selectedColor :string = '';

  actionIndexes :ActionIndex<string, number> = {};
  fieldType = EFieldType;

  availableActions: any[] = [];
  private onDestroy$ = new Subject();

  constructor(
    private fb :UntypedFormBuilder,
    private snackBar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.listenEventChanges();
    this.listenConditionChanges();

    this.setActionIndexes();

    for (let i = 0; i < this.form.controls['actions'].value.length; i++) {
      if (this.form.controls['actions']?.value[i]?.action === 'updateColumn') {
        this.columnFieldValues(
          this.form.controls['actions']?.value[i]?.updateColumn?.field,
        );
        this.customSelected =
          this.form.controls['actions']?.value[i]?.updateColumn?.field;
      }
    }

    this.buildAggregateAttributes(false);
    this.getContextValues();
    this.setSelectedColor();

    // Set available actions initially
    this.updateAvailableActions();

    // Listen for event changes to update available actions
    this.form.get('event')?.valueChanges.pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(() => {
      this.updateAvailableActions();
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  ngOnChanges(changes :SimpleChanges) :void {
    if (changes['customFields'].currentValue) {
      this.customAggregateAttributes = [];
      this.buildAggregateAttributes(true);
    }
  }

  columnFieldValues(columnId :string) :void {
    for (const key in this.automationRuleConfig.updateColumnFields) {
      if (this.automationRuleConfig.updateColumnFields[key].id === columnId) {
        this.columnFields = [
          ...this.automationRuleConfig.updateColumnFields[key].values,
        ];
        break;
      }
    }
  }

  onAddActionRow() :void {
    this.actionsFormArray.push(
      this.fb.group({
        action: ['', Validators.required],
      }),
    );
    this.setActionIndexes();
  }

  onRemoveAction(index :number) :void {
    this.actionsFormArray.removeAt(index);
    this.setActionIndexes();
  }

  onActionChange(action :string, index :number) :void {
    const actionFormGroup = <UntypedFormGroup>this.actionsFormArray.at(index); // get the first FormGroup
    const controlNames = Object.keys(actionFormGroup.controls);

    for (const controlName of controlNames) {
      if (controlName !== 'action') {
        actionFormGroup.removeControl(controlName);
      }
    }

    switch (action) {
      case EAutomationRuleActions.ASSIGN_TAG:
        actionFormGroup.addControl(
          'assignTag',
          this.fb.group({
            tags: this.fb.array([], Validators.required),
          }),
        );
        break;
      case EAutomationRuleActions.UPDATE_COLUMN:
        this.getContextValues();
        actionFormGroup.addControl(
          'updateColumn',
          this.fb.group({
            field: ['', Validators.required],
            customField: [''],
            value: ['', Validators.required],
            type: [''],
            options: [null],
            defaultOptions: [null],
          }),
        );
        break;
      case EAutomationRuleActions.AGGREGATE_RELATIONSHIP:
        actionFormGroup.addControl(
          'aggregateRelationship',
          this.fb.group({
            relation: ['', Validators.required],
            aggregate: ['', Validators.required],
            attribute: ['', Validators.required],
          }),
        );
        break;

      case EAutomationRuleActions.EMAIL:
        actionFormGroup.addControl(
          'email',
          this.fb.group({
            template: ['', Validators.required],
            email: ['', Validators.required],
          }),
        );
        break;

      case EAutomationRuleActions.HOLD_ORDER:
        actionFormGroup.addControl(
          'holdOrder',
          this.fb.group({
            holdUntil: ['', Validators.required],
            holdType: ['', Validators.required],
          }),
        );
        break;

      case EAutomationRuleActions.COMPLETE_ASSEMBLY_ORDER:
        actionFormGroup.addControl(
          'completeAssemblyOrder',
          this.fb.group({
            type: [EAutomationRuleActionTypes.COMPLETE_ASSEMBLY_ORDER],
            value: this.fb.group({
              value: [EAutomationRuleActionTypes.COMPLETE_ASSEMBLY_ORDER]
            })
          }),
        );
        break;

      case EAutomationRuleActions.SEND_EDI_855:
        actionFormGroup.addControl('sendEdi855', this.fb.group({}));
        break;

      case EAutomationRuleActions.SEND_UNALLOCATED_ORDERS_IN_PURGATORY_EMAIL:
        actionFormGroup.addControl(
          EAutomationRuleActions.SEND_UNALLOCATED_ORDERS_IN_PURGATORY_EMAIL,
          this.fb.group({
            userGroups: this.fb.array([], Validators.required),
          }),
        );

        if (!this.hasUserGroups) {
          this.showNoUserGroupsError();
        }
        break;

      case EAutomationRuleActions.SEND_INBOUND_SHIPMENT_EMAIL:
        actionFormGroup.addControl(
          EAutomationRuleActions.SEND_INBOUND_SHIPMENT_EMAIL,
          this.fb.group({
            userGroups: this.fb.array([], Validators.required),
          }),
        );

        if (!this.hasUserGroups) {
          this.showNoUserGroupsError();
        }
        break;

      default:
        break;
    }

    this.setActionIndexes();
  }

  onSelectTag(event :any, index :number) :void {
    const actionFormGroup = <UntypedFormGroup>this.actionsFormArray.at(index); // get the first FormGroup
    const tagsFormArray = <UntypedFormArray>actionFormGroup.get('assignTag.tags');
    tagsFormArray.clear();

    event.tag.forEach((tag :number) => {
      tagsFormArray.push(this.fb.control(tag));
    });
    tagsFormArray.markAsTouched();
  }

  onSelectUserGroupUnallocatedOrders(event :any, index :number) :void {
    const actionFormGroup = <UntypedFormGroup>this.actionsFormArray.at(index); // get the first FormGroup
    const sendUnallocatedOrdersInPurgatoryEmailFormArray = <UntypedFormArray>(
      actionFormGroup.get('sendUnallocatedOrdersInPurgatoryEmail.userGroups')
    );

    sendUnallocatedOrdersInPurgatoryEmailFormArray?.clear();

    event.userGroup.forEach((userGroup :number) => {
      if (sendUnallocatedOrdersInPurgatoryEmailFormArray) {
        sendUnallocatedOrdersInPurgatoryEmailFormArray.push(
          this.fb.control(userGroup),
        );
      }
    });
    if (sendUnallocatedOrdersInPurgatoryEmailFormArray) {
      sendUnallocatedOrdersInPurgatoryEmailFormArray.markAsTouched();
    }
  }

  onSelectUserGroupInboundShipment(event :any, index :number) :void {
    const actionFormGroup = <UntypedFormGroup>this.actionsFormArray.at(index); // get the first FormGroup
    const sendInboundShipmentEmailFormArray = <UntypedFormArray>(
      actionFormGroup.get('sendInboundShipmentEmail.userGroups')
    );

    sendInboundShipmentEmailFormArray?.clear();

    event.userGroup.forEach((userGroup :number) => {
      if (sendInboundShipmentEmailFormArray) {
        sendInboundShipmentEmailFormArray.push(this.fb.control(userGroup));
      }
    });
    if (sendInboundShipmentEmailFormArray) {
      sendInboundShipmentEmailFormArray.markAsTouched();
    }
  }

  get actionsFormArray() :UntypedFormArray {
    return this.form.get('actions') as UntypedFormArray;
  }

  buildAggregateAttributes(customLoaded :boolean) :void {
    if (!customLoaded) {
      this.aggregateAttributes = this.automationRuleConfig.aggregateAttributes;
    } else {
      this.aggregateAttributes = [];
      this.customFields.forEach((custom) => {
        const customField = {
          id: `custom_field_${custom.label.toLowerCase()}`,
          name: custom.label,
          type: `custom_field_${custom.label.toLowerCase()}`,
        };
        this.customAggregateAttributes.push(customField);
      });

      this.aggregateAttributes = [
        ...this.automationRuleConfig.aggregateAttributes,
        ...this.customAggregateAttributes,
      ];
    }

    for (let i = 0; i < this.form.controls['actions'].value.length; i++) {
      if (
        this.form.controls['actions']?.value[i]?.action === 'aggregateRelationship'
      ) {
        this.setAttributeRelation(
          this.form.controls['actions'].value[i].aggregateRelationship['relation'],
          false,
        );
      }
    }

    this.actionsFormArray.controls.forEach((control :any, index :number) => {
      if (
        this.customFields.length > 0 &&
        control.value?.action === 'updateColumn' &&
        control.value?.updateColumn?.customField
      ) {
        const originalString = control.value.updateColumn.customField ?? '';
        const prefixToRemove = 'custom_field_';
        const modifiedString = originalString.replace(
          new RegExp(`^${prefixToRemove}`),
          '',
        );

        const customField = this.customFields.find(
          (custom) => custom.label === modifiedString,
        );
        if (customField) {
          control.get('updateColumn.type').setValue(customField.type);
          if (
            control?.value?.updateColumn?.type === EFieldType.DROPDOWN &&
            customField.options?.length > 0
          ) {
            const options = customField.options.map((option) => {
              return {
                label: option,
                value: option,
              };
            });
            const selectedOption =
              options?.find(
                (option) => option.value === control.value?.updateColumn?.value,
              ) ?? null;
            control.get('updateColumn.value').setValue(selectedOption);
            control.get('updateColumn.options').setValue(options ?? null);
            control.get('updateColumn.defaultOptions').setValue(options ?? null);
          }
        }
      }
    });
  }

  getContextValues() :void {
    this.contextValues = '';
    for (let i = 0; i < this.form.controls['actions'].value.length; i++) {
      if (
        this.form.controls['actions'].value[i]?.action === 'aggregateRelationship'
      ) {
        const agg :any =
          this.form.controls['actions'].value[i].aggregateRelationship['aggregate'];
        const attr :string =
          this.form.controls['actions'].value[i].aggregateRelationship['attribute'];
        let field :string;

        if (attr.includes('custom_')) {
          field = attr.split('.')[1].split('_')[2].toLowerCase();
        } else {
          field = '';
        }

        this.contextValues +=
          '{{ context.' + agg + '_' + field + '.' + 'response }}';
      }
    }

    if (
      this.eventControl?.value === EAutomationRuleEvents.DAILY ||
      this.eventControl?.value === EAutomationRuleEvents.HOURLY
    ) {
      this.setDailyHourlyActions();
    }
  }

  onRelationChange(value :string) :void {
    this.attributeRelation = value;
    this.setAttributeRelation(value, true);
  }

  setActionIndexes() :void {
    this.actionIndexes['updateColumn'] = this.form
      .get('actions')
      ?.value.findIndex((obj :any) => obj?.action === 'updateColumn');
    this.actionIndexes['email'] = this.form
      .get('actions')
      ?.value.findIndex((obj :any) => obj?.action === 'email');
  }

  setAttributeRelation(relationValue :string, relationChanging :boolean) :void {
    let relation :string = '';
    if (relationChanging) {
      relation = relationValue;
    } else {
      this.automationRuleConfig.aggregateRelations.forEach((agRelation) => {
        if (agRelation.id === relationValue) {
          relation = agRelation.context;
        }
      });
    }

    this.aggregateAttributes.forEach((attr) => {
      const baseId = attr.id.includes('.') ? attr.id.split('.')[1] : attr.id;
      attr.id = `${relation}.${baseId}`;
    });
  }

  setSelectedColor() :void {
    if (this.actionIndexes['updateColumn'] > -1) {
      if (
        this.form
          .get('actions')
          ?.get(this.actionIndexes['updateColumn'].toString())
          ?.get('updateColumn.field')?.value === 'backgroundColor'
      ) {
        this.selectedColor = this.form
          .get('actions')
          ?.get(this.actionIndexes['updateColumn'].toString())
          ?.get('updateColumn.value')?.value;
      }
    }
  }

  onColorPickerChange(color :string) :void {
    this.form
      .get('actions')
      ?.get(this.actionIndexes['updateColumn'].toString())
      ?.get('updateColumn.value')
      ?.setValue(color);
  }

  contextEvent(
    e :MouseEvent,
    trigger :MatMenuTrigger,
    spanTrigger :HTMLElement,
  ) :void {
    spanTrigger.style.left = e.clientX + 5 + 'px';
    spanTrigger.style.top = e.clientY + 5 + 'px';
    if (trigger.menuOpen) {
      trigger.closeMenu();
      trigger.openMenu();
    } else {
      trigger.openMenu();
    }
    e.preventDefault();
  }

  insertVariable(e :Event, variable :string) :void {
    const template = this.form
      .get('actions')
      ?.get(this.actionIndexes['email'].toString())
      ?.get('email.template');
    switch (variable) {
      case 'orderNumber':
        const orderNumberTemplate = template?.value + '{{ orderNumber }}';
        template?.setValue(orderNumberTemplate);
        break;

      case 'invoiceId':
        const invoiceTemplate = template?.value + '{{ id }}';
        template?.setValue(invoiceTemplate);
        break;

      default:
        break;
    }
  }

  onClickCustomField(customField :any, index :number) :void {
    const actionControl = this.actionsFormArray.at(index);
    actionControl.get('updateColumn.type').setValue(customField.type);
    if (customField.options?.length > 0) {
      const options = customField.options.map((option) => {
        return {
          label: option,
          value: option,
        };
      });
      actionControl.get('updateColumn.options').setValue(options ?? null);
      actionControl.get('updateColumn.defaultOptions').setValue(options ?? null);
    }
    actionControl.get('updateColumn.value').setValue('');
    actionControl.get('updateColumn.value').markAsTouched();
    actionControl.get('updateColumn.value').updateValueAndValidity();
  }

  onDropdownInputChangeEvent(event :any, index :number) {
    const actionControl = this.actionsFormArray.at(index);
    const options = actionControl.get('updateColumn.defaultOptions').value;
    if (options?.length > 0) {
      const lowerCaseKeyword = event.toLowerCase();
      actionControl
        .get('updateColumn.options')
        .setValue(
          options.filter((option) =>
            option.value.toLowerCase().includes(lowerCaseKeyword),
          ),
        );
    }
    actionControl.get('updateColumn.value').setValue('');
  }

  private showNoUserGroupsError() :void {
    this.snackBar.open('No user groups found. Create one to proceed.', 'Error', {
      duration: SnackBarConfig.DEFAULT.duration,
    });
  }

  private listenEventChanges() :void {
    this.form.get('event')?.valueChanges.subscribe((eventValue :string) => {
      // Will reset actions on every select of event
      this.resetActions();

      if (
        eventValue === EAutomationRuleEvents.DAILY ||
        eventValue === EAutomationRuleEvents.HOURLY
      ) {
        this.setDailyHourlyActions();
        this.clearConditions();
      } else if (['order_created', 'order_updated'].includes(eventValue)) {
        this.addSendEdi855Action();
        this.removeActions([
          EAutomationRuleActions.SEND_UNALLOCATED_ORDERS_IN_PURGATORY_EMAIL,
          EAutomationRuleActions.SEND_INBOUND_SHIPMENT_EMAIL,
        ]);
      } else {
        this.removeActions([
          EAutomationRuleActions.SEND_EDI_855,
          EAutomationRuleActions.SEND_UNALLOCATED_ORDERS_IN_PURGATORY_EMAIL,
          EAutomationRuleActions.SEND_INBOUND_SHIPMENT_EMAIL,
        ]);
        this.filterActions([
          EAutomationRuleActions.SEND_EDI_855,
          EAutomationRuleActions.SEND_UNALLOCATED_ORDERS_IN_PURGATORY_EMAIL,
          EAutomationRuleActions.SEND_INBOUND_SHIPMENT_EMAIL,
        ]);
      }
    });
  }

  private resetActions() :void {
    this.automationRuleConfig.actions = this.rawAutomationRuleConfigActions;
  }

  private setDailyHourlyActions() :void {
    this.automationRuleConfig = {
      ...this.automationRuleConfig,
      actions: [
        {
          id: EAutomationRuleActions.SEND_UNALLOCATED_ORDERS_IN_PURGATORY_EMAIL,
          name: 'Send Unallocated Orders In Purgatory Email',
          type: 'send_unallocated_orders_in_purgatory_email',
        },
        {
          id: EAutomationRuleActions.SEND_INBOUND_SHIPMENT_EMAIL,
          name: 'Send Inbound Shipment Email',
          type: 'send_inbound_shipment_email',
        },
      ],
    };
  }

  private clearConditions() :void {
    const conditions = this.form.get('conditions') as UntypedFormArray;
    if (conditions) {
      conditions.clear();
    }
  }

  private addSendEdi855Action() :void {
    this.automationRuleConfig = {
      ...this.automationRuleConfig,
      actions: [
        ...this.automationRuleConfig.actions.filter(
          (action) => action.id !== EAutomationRuleActions.SEND_EDI_855,
        ),
        {
          id: EAutomationRuleActions.SEND_EDI_855,
          name: 'Send EDI 855',
          type: 'send_edi_855',
        },
      ],
    };
  }

  private removeActions(actionIds :string[]) :void {
    const actions = this.form.get('actions') as UntypedFormArray;
    if (actions) {
      actionIds.forEach((actionId) => {
        const indexToRemove = actions.controls.findIndex(
          (control) => control.value.action === actionId,
        );
        if (indexToRemove !== -1) {
          actions.removeAt(indexToRemove);
        }
      });
    }
  }

  private filterActions(excludedIds :string[]) :void {
    this.automationRuleConfig = {
      ...this.automationRuleConfig,
      actions: this.automationRuleConfig.actions.filter(
        (action) => !excludedIds.includes(action.id),
      ),
    };
  }

  get eventControl() :UntypedFormGroup {
    return this.form.get('event') as UntypedFormGroup;
  }

  get addActionControl() :any {
    return this.actionsFormArray;
  }

  private listenConditionChanges() :void {
    const conditions = this.form.get('conditions') as UntypedFormArray;
    if (conditions) {
      conditions.valueChanges.subscribe(() => {
        this.updateActionsBasedOnConditions();
      });
    }
  }

  private updateActionsBasedOnConditions() :void {
    const conditions = this.form.get('conditions') as UntypedFormArray;
    if (!conditions) return;

    // Reset actions to original state first
    this.resetActions();

    // Check if any BOM-related conditions exist
    const hasBomConditions = conditions.controls.some(control => {
      const condition = control.get('condition')?.value;
      return condition === EAutomationRuleConditions.BOM_ITEM_RECEIVED ||
             condition === EAutomationRuleConditions.BOM_STOCK_AVAILABILITY;
    });

    if (hasBomConditions) {
      // Filter to show only Complete Assembly Order action
      this.automationRuleConfig = {
        ...this.automationRuleConfig,
        actions: [
          {
            id: EAutomationRuleActions.COMPLETE_ASSEMBLY_ORDER,
            name: 'Complete Assembly Order',
            type: 'complete_assembly_order',
          }
        ],
      };

      // Remove any non-Complete Assembly Order actions from the form
      this.removeNonBomActions();
    } else {
      // Remove Complete Assembly Order action if no BOM conditions
      this.removeActions([EAutomationRuleActions.COMPLETE_ASSEMBLY_ORDER]);
      this.filterActions([EAutomationRuleActions.COMPLETE_ASSEMBLY_ORDER]);
    }
  }

  private removeNonBomActions() :void {
    const actions = this.form.get('actions') as UntypedFormArray;
    if (actions) {
      const actionsToKeep = actions.controls.filter(
        control => control.get('action')?.value === EAutomationRuleActions.COMPLETE_ASSEMBLY_ORDER
      );
      
      actions.clear();
      actionsToKeep.forEach(control => actions.push(control));
    }
  }

  private updateAvailableActions(): void {
    const event = this.form.get('event')?.value;
    if (event === EAutomationRuleEvents.RECEIVING) {
      this.availableActions = [
        {
          id: EAutomationRuleActions.COMPLETE_ASSEMBLY_ORDER,
          name: 'Complete Assembly Order',
          type: 'complete_assembly_order',
        },
      ];
    } else {
      this.availableActions = this.automationRuleConfig.actions;
    }
  }
}
